import React from 'react';
import { useQuery } from '@apollo/react-hooks';

import { FETCH_SUBSCRIPTION } from 'graphql/subs';
import { Subscription, Plan, Price, PaymentMethod } from 'types/subscription';

type ISubscriptionContext = {
  loading: boolean;
  error?: string;
  subscription?: Subscription;
  paymentMethod?: PaymentMethod;
  plan?: Plan;
  price?: Price;
};

export const SubscriptionContext = React.createContext<ISubscriptionContext>(
  {} as ISubscriptionContext,
);

const SubscriptionProvider: React.FC = ({ children }) => {
  const [subscription, setSubscription] = React.useState<Subscription>();
  const [plan, setPlan] = React.useState<Plan>();
  const [price, setPrice] = React.useState<Price>();
  const [paymentMethod, setPaymentMethod] = React.useState<PaymentMethod>();
  const [error, setError] = React.useState('');
  const { loading, error: apolloError, data } = useQuery(FETCH_SUBSCRIPTION);

  const extractPlanAndPrice = (s: Subscription) => {
    const { data } = s.items;
    const item = data[0];
    setPlan(item.plan);
    setPrice(item.price);
  };

  const extractPaymentMethod = (s: Subscription) => {
    const { defaultPaymentMethod: subDefaultPM, customer } = s;
    const { defaultPaymentMethod: customerDefaultPM } = customer.invoiceSettings;
    const pmToUse = subDefaultPM !== null ? subDefaultPM : customerDefaultPM;
    setPaymentMethod(pmToUse);
  };

  React.useEffect(() => {
    if (!loading && data?.subscription) {
      setSubscription(data.subscription);
    }
  }, [loading, data]);

  // Extract plan and price info
  React.useEffect(() => {
    if (!loading && subscription) {
      extractPlanAndPrice(subscription);
      extractPaymentMethod(subscription);
    }
  }, [loading, subscription]);

  React.useEffect(() => {
    apolloError && apolloError?.message && setError(apolloError.message);
  }, [apolloError]);

  return (
    <SubscriptionContext.Provider
      value={{
        loading,
        error,
        subscription,
        paymentMethod,
        plan,
        price,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionProvider;
